import { useState, useEffect } from 'react';
import { useCreateGroup, useUpdateGroup } from '../../hooks/useGroups';
import { useServices } from '../../hooks/useServices';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input, checkboxClass } from '../common/Input';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';
import { Group } from '../../types';


interface Props {
  group?: Group;
  onClose: () => void;
}

export function GroupForm({ group, onClose }: Props) {
  const isEdit = Boolean(group);
  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup();
  const { data: services } = useServices();
  const { addToast } = useToast();

  const [form, setForm] = useState({
    name: '',
    description: '',
    sort_order: 0,
    service_ids: [] as number[],
  });

  useEffect(() => {
    if (group) {
      setForm({
        name: group.name,
        description: group.description || '',
        sort_order: group.sort_order,
        service_ids: group.service_ids || [],
      });
    }
  }, [group]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data = {
      name: form.name,
      description: form.description || null,
      sort_order: form.sort_order,
      service_ids: form.service_ids,
    };

    if (isEdit && group) {
      updateGroup.mutate({ id: group.id, data }, {
        onSuccess: () => { addToast('success', 'Group updated'); onClose(); },
        onError: err => addToast('error', apiError(err, 'Failed to update group')),
      });
    } else {
      createGroup.mutate(data, {
        onSuccess: () => { addToast('success', 'Group created'); onClose(); },
        onError: err => addToast('error', apiError(err, 'Failed to create group')),
      });
    }
  };

  const update = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field label="Name" required>
        {p => (
          <Input
            {...p}
            type="text"
            required
            value={form.name}
            onChange={e => update('name', e.target.value)}
            placeholder="Production Services"
          />
        )}
      </Field>

      <Field label="Description">
        {p => (
          <Input
            {...p}
            type="text"
            value={form.description}
            onChange={e => update('description', e.target.value)}
            placeholder="Main production services"
          />
        )}
      </Field>

      <Field label="Sort Order">
        {p => (
          <Input
            {...p}
            type="number"
            min={0}
            value={form.sort_order}
            onChange={e => {
              const n = parseInt(e.target.value, 10);
              update('sort_order', Number.isNaN(n) ? 0 : n);
            }}
          />
        )}
      </Field>

      {services && services.length > 0 && (
        <fieldset>
          <legend className="block text-sm font-medium mb-1">Services</legend>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {services.map(s => (
              <label key={s.id} className="inline-flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={form.service_ids.includes(s.id)}
                  onChange={e => {
                    if (e.target.checked) update('service_ids', [...form.service_ids, s.id]);
                    else update('service_ids', form.service_ids.filter(id => id !== s.id));
                  }}
                  className={checkboxClass}
                />
                {s.name}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={createGroup.isPending || updateGroup.isPending}>
          {isEdit ? 'Update' : 'Create'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}
